import { Navigate, useNavigate } from 'react-router'
import { useAuth } from '../../hooks/useAuth'
import { useOrganization } from '../../hooks/useOrganization'
import { ROLES, ROLE_HIERARCHY, formatRoleLabel } from '../../constants/roles'
import { organizationLandingPath } from '../../config/studioRoutes'
import { Button } from './Button'
import { LoadingSpinner } from './LoadingSpinner'
import { StatePanel } from './StatePanel'
import styles from './ProtectedRoute.module.css'

const HIGHEST_ROLE_LEVEL = Math.max(...Object.values(ROLE_HIERARCHY))

// Organization users (roles below internal_org_admin) never see Studio's role
// or permission model: they get one plain sentence and a way back.
export const ORGANIZATION_USER_DENIED_MESSAGE = "You don't have access to this page. Ask your organization admin if you need it."

// requiredRoleMessage names the role a page needs by its label, never by its
// code: "You need the External Org Admin role or higher to open this page."
function requiredRoleMessage(role) {
  const orHigher = (ROLE_HIERARCHY[role] || 0) < HIGHEST_ROLE_LEVEL ? ' or higher' : ''
  return `You need the ${formatRoleLabel(role)} role${orHigher} to open this page.`
}

function DeniedPanel({ description, backPath }) {
  const navigate = useNavigate()
  return (
    <div className={styles.fullPage}>
      <StatePanel
        state="restricted"
        title="Access Denied"
        description={description}
        action={backPath ? (
          <Button variant="secondary" onClick={() => navigate(backPath)}>Back to Studio</Button>
        ) : null}
      />
    </div>
  )
}

function ProtectedRoute({ children, requiredRole, requiredGlobalRole, requiredPermission, requireCommandCenterAccess = false }) {
  const { isAuthenticated, loading, hasRole, commandCenterAccess, organizations = [] } = useAuth()
  const { currentOrg, hasPermission } = useOrganization()

  if (loading) {
    return (
      <div className={styles.fullPage}>
        <LoadingSpinner message="Loading..." />
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  const internalUser = hasRole(ROLES.INTERNAL_ORG_ADMIN)
  const backPath = organizationLandingPath(currentOrg)
  const deniedReason = (internalReason) => (internalUser ? internalReason : ORGANIZATION_USER_DENIED_MESSAGE)

  if (requiredRole && !hasRole(requiredRole)) {
    return <DeniedPanel description={deniedReason(requiredRoleMessage(requiredRole))} backPath={backPath} />
  }

  if (requiredGlobalRole && !organizations.some((organization) => (
    organization.role === requiredGlobalRole
  ))) {
    return <DeniedPanel description={deniedReason('Platform-wide user access requires a super admin role.')} backPath={backPath} />
  }

  if (requireCommandCenterAccess && !commandCenterAccess) {
    return <DeniedPanel description="You don't have access to Command Center." backPath={backPath} />
  }

  if (requiredPermission && !hasPermission(requiredPermission)) {
    return (
      <DeniedPanel
        description={deniedReason(`You don't have the required permission to access this page. Required permission: ${requiredPermission}`)}
        backPath={backPath}
      />
    )
  }

  return children
}

export default ProtectedRoute
